import { type JanusCtx, type Row, handBack, itemField, refuse, upper, definitionNotHeld } from './context.js';
import type { JanusInput } from './janus.js';
import { abilityChargeEntry, abilityEntryId } from './charges/charge-entry.js';
import { adjustCharges } from './charges/adjust-charges.js';
import { layerListEntryUuid } from '../actions/spell-components.js';
import { chargeReductionDelta } from '../resources/reduction-guard.js';
import { resourceReductionReaders } from './resource-reduction.js';

/** An innate branch spends the granting parent's pool, as template_spell_roll.php does. */
function innateSpellPoolId(ctx: JanusCtx, ability: Row, caster: Row): string {
  const id = upper(itemField(ability, 'item_id'));
  const entries = ctx.read.entries(caster, 'innate_spell_per_long_rest');
  if (entries.some(entry => abilityEntryId(entry) === id)) return id;
  for (const entry of entries) {
    const parentId = abilityEntryId(entry);
    if (!parentId) handBack('innate_spell_per_long_rest:grant-not-held');
    const parent = ctx.read.definition(parentId);
    if (!parent) definitionNotHeld('innate_spell_per_long_rest:definition-not-held', parentId);
    if (ctx.read.entries(parent, 'ability_choice').some(child => upper(layerListEntryUuid(child)) === id)) return parentId;
  }
  handBack('innate_spell_per_long_rest:pool-not-held');
}

function innateCast(ctx: JanusCtx, input: JanusInput): boolean {
  return input.useAbilityVariable1 === 'innate_spell_per_long_rest'
    || (input.useAbilityVariable1 === 'finish_casting' && input.useAbilityVariable2 === 'innate_spell_per_long_rest'
      && ctx.timedFinish?.casterId === upper(input.casterId) && ctx.timedFinish.abilityId === upper(input.abilityId));
}

export function preflightInnateSpellUse(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void {
  if (!innateCast(ctx, input)) return;
  const { current } = abilityChargeEntry(ctx, caster, innateSpellPoolId(ctx, ability, caster));
  if (current < -chargeReductionDelta(caster, -1, resourceReductionReaders(ctx))) refuse('No uses left — this recharges on a long rest.');
}

/** Kept outside the countered branch: a counterspell preserves the innate use. */
export function settleInnateSpellUse(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void {
  if (innateCast(ctx, input))
    adjustCharges(ctx, caster, innateSpellPoolId(ctx, ability, caster), -1, true);
}
